import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  GetEquipmentsWorkOrderById,
  UpdatEquipmentsWorkOrder,
  confirmWorkOrder,
  GetEquipmentsWorkOrderList
} from '@store/FLEET/EquipmentsWorkOrder/equipmentsworkorderSlice';
import { Card, CardBody, CardHeader } from '@components/card/card';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import { useFormik } from 'formik';
import { Form, Badge, Nav, Modal, Alert, Button, InputGroup } from 'react-bootstrap'; // إضافة Badge لعرض الحالات
import { useTranslation } from 'react-i18next';
import { UpdateWorkOrderSchema } from '../../ValidationForm/validationSchema';
import Loading from '@components/Shared/Loader/Loading';
import Lines from './Lines';
import PersonLines from './PersonLines';
import { usePermissions } from '@Helper/Permisstions/usePermissions';

const ViewWorkOrder = ({ Id, onClose }: { Id: string; onClose: () => void }) => {
  const { HasPermission } = usePermissions();

  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('basic-info');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (Id) {
      dispatch(GetEquipmentsWorkOrderById(Id));
    }
  }, [Id, dispatch]);

  const {
    EquipmentsWorkOrderById: data,
    loadingById: loading,
    error
  } = useAppSelector(state => state.EquipmentsWorkOrder);

  // دالة لتأكيد أمر الصيانة
  const handleConfirmWorkOrder = () => {
    setShowConfirmModal(false);
    if (Id) {
      dispatch(confirmWorkOrder(Id))
        .unwrap()
        .then(res => {
          if (res?.succeeded === true && res.data === true) {
            CustomAlert({ action: 'Add', msg: t('WorkOrderConfirmedSuccessfully') });
            // إعادة تحميل البيانات لرؤية التغييرات
            dispatch(GetEquipmentsWorkOrderList());
            onClose();
          } else {
            CustomAlert({ action: 'Add', msg: res?.message || t('ConfirmationFailed') });
          }
        })
        .catch((error: any) => {
          CustomAlert({ action: 'Add', msg: t('NetworkError') });
        });
    }
  };

  const formik = useFormik({
    initialValues: {
      id: Id,
      fleetEquipmentsWorkRequestsId: data ? data.fleetEquipmentsWorkRequestsId : '',
      fleetEquipmentsId: data ? data.fleetEquipmentsId : '',
      orderCode: data ? data.orderCode : '',
      orderType: data ? data.orderType : '',
      note: data ? data.note : '',
      odometer: data ? data.odometer : '',
      organizationId: data ? data.organizationId : '',
      closePersonId: data ? data.closePersonId : '',
      dateCompletion: data ? data.dateCompletion : '',
      isInternal: data ? data.isInternal : '', // boolean
      startDateSugg: data ? data.startDateSugg?.split('T')[0] : '',
      endDateSugg: data ? data.endDateSugg?.split('T')[0] : '',
      totalItems: data ? data.totalItems : '',
      totalAsset: data ? data.totalAsset : '',
      otherAmounts: data ? data.otherAmounts : '',
      totalBudget: data ? data.totalBudget : '',
      supplierId: data ? data.supplierId : '',
      workshopId: data ? data.workshopId : '',
      startDateExecution: data ? data.startDateExecution : '',
      endDateExecution: data ? data.endDateExecution : '',
      noteCompletion: data ? data.noteCompletion : '',
      totalItemsActual: data ? data.totalItemsActual : '',
      totalAssetActual: data ? data.totalAssetActual : '',
      otherAmountsActual: data ? data.otherAmountsActual : '',
      totalBudgetActual: data ? data.totalBudgetActual : '',

      inventoryFlag: data ? data.inventoryFlag : false,
      isConfirmed: data ? data.isConfirm : false, // إضافة حالة التأكيد
      isClosed: data ? data.dateCompletion !== null : false, // إضافة حالة الإغلاق

      equipmentsWorkOrderItemList: data
        ? data.equipmentsWorkOrderItemList.map(m => ({
            id: m.id,
            itemId: m.itemId,
            uom: m.uom,
            qty: Number(m.qty),
            unitCost: Number(m.unitCost),
            totalCost: Number(m.totalCost),
            notes: m.notes
          }))
        : [],

      equipmentsWorkOrderPersonList: data
        ? data.equipmentsWorkOrderPersonList.map(m => ({
            id: m.id,
            personId: m.personId,
            notes: m.notes || ''
          }))
        : []
    },
    enableReinitialize: true,
    validationSchema: UpdateWorkOrderSchema(t),
    onSubmit: values => {
      const successMessage = {
        ar: 'تم إغلاق أمر الصيانة بنجاح',
        en: 'Maintenance order closed successfully'
      };
      dispatch(
        UpdatEquipmentsWorkOrder({
          id: data?.id,
          fleetEquipmentsWorkRequestsId: values.fleetEquipmentsWorkRequestsId,
          fleetEquipmentsId: values.fleetEquipmentsId,
          orderCode: values.orderCode,
          orderType: values.orderType,
          odometer: values.odometer || 0,
          closePersonId: localStorage.personId ? localStorage.personId : null,
          dateCompletion: values.dateCompletion,
          isInternal: values.isInternal,
          startDateSugg: values.startDateSugg,
          endDateSugg: values.endDateSugg,
          totalItems: values.totalItems,
          totalAsset: values.totalAsset,
          otherAmounts: values.otherAmounts,
          totalBudget: values.totalBudget,
          supplierId: values.supplierId,
          workshopId: values.workshopId,
          startDateExecution: values.startDateExecution,
          endDateExecution: values.endDateExecution,
          noteCompletion: values.noteCompletion,
          totalItemsActual: values.totalItemsActual,
          totalAssetActual: values.totalAssetActual,
          otherAmountsActual: values.otherAmountsActual,
          totalBudgetActual: values.totalBudgetActual,
          note: values.note,
          organizationId: values.organizationId,

          equipmentsWorkOrderItemList:
            values.equipmentsWorkOrderItemList.map(m => ({
              id: m.id || null,
              itemId: m.itemId,
              uom: m.uom,
              qty: Number(m.qty),
              unitCost: Number(m.unitCost),
              totalCost: Number(m.totalCost),
              notes: m.notes
            })) ?? [],

          equipmentsWorkOrderPersonList:
            values.equipmentsWorkOrderPersonList.map(m => ({
              id: m.id || null,
              personId: m.personId || null,
              notes: m.notes || ''
            })) ?? []
        })
      )
        .unwrap()
        .then(res => {
          if (res?.succeeded === true) {
            formik.resetForm();
            CustomAlert({ action: 'Edit', msg: i18n.language === 'en' ? successMessage.en : successMessage.ar });
          } else {
            formik.setSubmitting(false);
            CustomAlert({ action: 'Error', msg: res?.message });
          }
        })
        .catch((error: any) => {
          formik.setSubmitting(false);
          CustomAlert({ action: 'Error' });
        });
    }
  });

  return (
    <>
      <Loading loading={loading} error={error}>
        <CardBody>
          <Card className='mb-3 p-3 shadow-sm'>
            <div className='d-flex justify-content-between align-items-start flex-wrap gap-2'>
              <div>
                <h4 className='m-0'>
                  <i className='fas fa-tools me-2 text-theme'></i>
                  {t('MaintenanceOrderDetails')}
                </h4>
                <small className='text-muted'>#{data?.orderCode}</small>
              </div>
              <div className='d-flex flex-wrap gap-2'>
                <Badge
                  bg={data?.isConfirm ? 'success' : 'white'}
                  text={data?.isConfirm ? 'white' : 'warning'}
                  className='p-2 d-flex align-items-center border border-warning'
                >
                  <i className={`fas ${data?.isConfirm ? 'fa-check-circle' : 'fa-clock'} me-1`}></i>
                  {data?.isConfirm ? t('Confirmed') : t('NotConfirmed')}
                </Badge>

                <Badge
                  bg={formik.values?.isClosed ? 'danger' : 'white'}
                  text={formik.values?.isClosed ? 'white' : 'info'}
                  className='p-2 d-flex align-items-center border border-info'
                >
                  <i className={`fas ${formik.values.isClosed ? 'fa-lock' : 'fa-lock-open'} me-1`}></i>
                  {formik.values.isClosed ? t('Closed') : t('Open')}
                </Badge>

                <Badge
                  bg={data?.inventoryFlag ? 'success' : 'white'}
                  text={data?.inventoryFlag ? 'white' : 'secondary'}
                  className='p-2 d-flex align-items-center border border-secondary'
                >
                  <i className={`fas ${data?.inventoryFlag ? 'fa-check-double' : 'fa-box-open'} me-1`}></i>
                  {data?.inventoryFlag ? t('MaterialsIssued') : t('MaterialsNotIssued_')}
                </Badge>
              </div>
            </div>
          </Card>

          {/* تبويبات التنقل */}
          <Nav
            variant='tabs'
            className='mb-4 flex-wrap'
            activeKey={activeTab}
            onSelect={k => setActiveTab(k || 'basic-info')}
          >
            <Nav.Item>
              <Nav.Link eventKey='basic-info'>
                <i className='fas fa-info-circle me-2'></i>
                {t('BasicInformation')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='items'>
                <i className='fas fa-list me-2'></i>
                {t('SpareParts')}
                <Badge bg='secondary' className='ms-2'>
                  {formik.values.equipmentsWorkOrderItemList.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='personnel'>
                <i className='fas fa-users me-2'></i>
                {t('MaintenanceTeam')}
                <Badge bg='secondary' className='ms-2'>
                  {formik.values.equipmentsWorkOrderPersonList.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='completion'>
                <i className='fas fa-check-circle me-2'></i>
                {t('Close_Maintenance_Order')}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {/* محتوى التبويبات */}
          <TabContent activeTab={activeTab}>
            {/* تبويب المعلومات الأساسية */}
            <TabPane tabId='basic-info'>
              <Card className='mb-4'>
                <CardHeader className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
                  <h5 className='m-0'>
                    <i className='fas fa-info-circle fa-fw me-2 text-theme'></i>
                    {t('BasicInformation')}
                  </h5>
                  {!data?.isConfirm && (
                    <button
                      hidden={!HasPermission('FleetConfirmMaintenanceOrder')}
                      type='button'
                      className='btn btn-success btn-sm   w-md-auto'
                      onClick={() => setShowConfirmModal(true)}
                    >
                      <i className='fas fa-check-circle me-2'></i>
                      {t('ConfirmWorkOrder')}
                    </button>
                  )}
                </CardHeader>
                <CardBody>
                  <div className='row'>
                    <div className='col-12 col-md-6 mb-3'>
                      <label className='form-label '>{t('OrderNumber')}</label>
                      <div className='form-control'>{formik.values.orderCode || t('NotSpecified')}</div>
                    </div>
                    <div className='col-12 col-md-6 mb-3'>
                      <label className='form-label '>{t('Vehicle')}</label>
                      <div className='form-control'>
                        {i18n.language === 'ar'
                          ? data?.fleetEquipmentsName
                          : data?.fleetEquipmentsName2 || t('NotSpecified')}
                      </div>
                    </div>
                    <div className='col-12 col-md-6 mb-3'>
                      <label className='form-label '>{data?.workshopId !== null ? t('Workshop') : t('Supplier')}</label>
                      <div className='form-control'>
                        {data?.workshopId !== null ? data?.workshopName : data?.supplierName || t('NotSpecified')}
                      </div>
                    </div>
                    <div className='col-12 col-md-6 mb-3'>
                      <label className='form-label '>{t('TypeMaintenance')}</label>
                      <div className='form-control'>{formik.values.orderType || t('NotSpecified')}</div>
                    </div>
                    <div className='col-12 col-md-6 mb-3'>
                      <label className='form-label '>{t('Expected_start_date')}</label>
                      <div className='form-control'>{formik.values.startDateSugg || t('NotSpecified')}</div>
                    </div>
                    <div className='col-12 col-md-6 mb-3'>
                      <label className='form-label '>{t('Expected_end_date')}</label>
                      <div className='form-control'>{formik.values.endDateSugg || t('NotSpecified')}</div>
                    </div>
                    <div className='col-12 mb-3'>
                      <label className='form-label '>{t('Note')}</label>
                      <div className='form-control' style={{ minHeight: '80px' }}>
                        {formik.values.note || t('NoNotesAvailable')}
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </TabPane>

            {/* تبويب المواد */}
            <TabPane tabId='items'>
              <Card className='mb-4'>
                <CardHeader>
                  <h5 className='m-0'>
                    <i className='fas fa-list me-2 text-theme'></i>
                    {t('SpareParts')}
                  </h5>
                </CardHeader>
                <CardBody>
                  <Lines formik={formik} isViewOnly={true} />
                </CardBody>
              </Card>
            </TabPane>

            {/* تبويب الفنيين */}
            <TabPane tabId='personnel'>
              <Card className='mb-4'>
                <CardHeader>
                  <h5 className='m-0'>
                    <i className='fas fa-users me-2 text-theme'></i>
                    {t('MaintenanceTeam')}
                  </h5>
                </CardHeader>
                <CardBody>
                  <PersonLines formik={formik} isViewOnly />
                </CardBody>
              </Card>
            </TabPane>

            {/* تبويب إتمام العمل */}
            <TabPane tabId='completion'>
              <Card className='mb-4'>
                <CardHeader>
                  <h5 className='m-0'>
                    <i className='fas fa-check-circle me-2 text-theme'></i>
                    {t('CLosingDetails')}
                  </h5>
                </CardHeader>
                <CardBody>
                  {data?.inventoryFlag !== true && data?.isInternal === true && (
                    <Alert variant='warning' className='mb-4'>
                      <div className='d-flex align-items-center'>
                        <i className='fas fa-exclamation-triangle me-3 fs-4'></i>
                        <div>
                          <h5 className='alert-heading'>{t('MaterialsNotIssued')}</h5>
                          <p className='mb-0'>{t('MaterialsNotIssuedMessage')}</p>
                        </div>
                      </div>
                    </Alert>
                  )}
                  {/* {data?.isInternal !== true && data?.isConfirm === false && (
                    <Alert variant='warning' className='mb-4'>
                      <div className='d-flex align-items-center'>
                        <i className='fas fa-exclamation-triangle me-3 fs-4'></i>
                        <div>
                          <h5 className='alert-heading'>{t('MaterialsNotIssued')}</h5>
                          <p className='mb-0'>{t('MaterialsNotIssuedMessage')}</p>
                        </div>
                      </div>
                    </Alert>
                  )} */}

                  {data?.isInternal !== true && data?.isConfirm === false && (
                    <Alert variant='warning' className='mb-4'>
                      <div className='d-flex align-items-center'>
                        <i className='fas fa-exclamation-triangle me-3 fs-4'></i>
                        <div>
                          <h5 className='alert-heading'>
                            {i18n.language === 'en'
                              ? 'External work order not confirmed'
                              : 'أمر الصيانة الخارجي غير مؤكد'}
                          </h5>
                          <p className='mb-0'>
                            {i18n.language === 'en'
                              ? 'This external work order does not require store issuance but has not been confirmed yet.'
                              : 'هذا أمر صيانة خارجي ولا يتطلب صرفًا من المخزن، لكنه لم يُؤكد بعد.'}
                          </p>
                        </div>
                      </div>
                    </Alert>
                  )}

                  {formik.values.isClosed ? (
                    <Alert variant='info' className='mb-4'>
                      <div className='d-flex align-items-center'>
                        <i className='fas fa-info-circle me-3 fs-4'></i>
                        <div>
                          <h5 className='alert-heading'>{t('OrderAlreadyClosed')}</h5>
                          <p className='mb-0'>
                            {t('OrderClosedOnDate')} :
                            <span className='fw-bold fs-5'>{formik.values.dateCompletion?.split('T')[0]}</span>
                          </p>
                        </div>
                      </div>
                    </Alert>
                  ) : (
                    <Form onSubmit={formik.handleSubmit}>
                      <div className='row'>
                        <div className='col-12 col-md-6 mb-3'>
                          <label className='form-label '>{t('actual_implementation_start_date')}</label>
                          <Form.Control
                            type='date'
                            name='startDateExecution'
                            onChange={formik.handleChange}
                            value={formik.values.startDateExecution || ''}
                            isInvalid={!!(formik.touched.startDateExecution && formik.errors.startDateExecution)}
                          />
                          <Form.Control.Feedback type='invalid'>
                            {formik.errors.startDateExecution}
                          </Form.Control.Feedback>
                        </div>

                        <div className='col-12 col-md-6 mb-3'>
                          <label className='form-label '>{t('actual_implementation_end_date')}</label>
                          <Form.Control
                            type='date'
                            name='endDateExecution'
                            onChange={formik.handleChange}
                            value={formik.values.endDateExecution || ''}
                            isInvalid={!!(formik.touched.endDateExecution && formik.errors.endDateExecution)}
                          />
                          <Form.Control.Feedback type='invalid'>{formik.errors.endDateExecution}</Form.Control.Feedback>
                        </div>

                        <div className='col-12 col-md-6 mb-3'>
                          <label className='form-label '>{t('DateCompletion')}</label>
                          <Form.Control
                            type='date'
                            name='dateCompletion'
                            onChange={formik.handleChange}
                            value={formik.values.dateCompletion || ''}
                            isInvalid={!!(formik.touched.dateCompletion && formik.errors.dateCompletion)}
                          />
                          <Form.Control.Feedback type='invalid'>{formik.errors.dateCompletion}</Form.Control.Feedback>
                        </div>

                        <div className='col-12 col-md-6 mb-3'>
                          <label className='form-label '>{t('Odometer')}</label>
                          <InputGroup>
                            <Form.Control
                              type='number'
                              name='odometer'
                              onChange={formik.handleChange}
                              value={formik.values.odometer || 0}
                              isInvalid={!!(formik.touched.odometer && formik.errors.odometer)}
                            />
                            <InputGroup.Text>{t('KM')}</InputGroup.Text>
                          </InputGroup>
                        </div>

                        <div className='col-12 col-md-3 mb-3'>
                          <label className='form-label '>{t('Other_amounts')}</label>
                          <Form.Control
                            type='number'
                            name='otherAmountsActual'
                            onChange={formik.handleChange}
                            value={formik.values.otherAmountsActual}
                            isInvalid={!!(formik.touched.otherAmountsActual && formik.errors.otherAmountsActual)}
                          />
                        </div>

                        <div className='col-12 col-md-9 mb-3'>
                          <label className='form-label '>{t('NoteComplate')}</label>
                          <Form.Control
                            as='textarea'
                            rows={1}
                            name='noteCompletion'
                            onChange={formik.handleChange}
                            value={formik.values.noteCompletion}
                            placeholder={t('AddCompletionNotes')}
                            isInvalid={!!(formik.touched.noteCompletion && formik.errors.noteCompletion)}
                          />
                          <Form.Control.Feedback type='invalid'>{formik.errors.noteCompletion}</Form.Control.Feedback>
                        </div>
                      </div>

                      <div className='d-flex justify-content-end flex-wrap gap-2 mt-4'>
                        {data?.inventoryFlag !== true ? (
                          <Alert variant='secondary' className='p-2   w-md-auto' hidden={data?.isInternal !== true}>
                            <i className='fas fa-info-circle me-2'></i>
                            {t('CannotCloseUntilMaterialsIssued')}
                          </Alert>
                        ) : (
                          <button
                            hidden={!HasPermission('FleetCloseMaintenanceOrder')}
                            disabled={formik.isSubmitting}
                            type='submit'
                            className='btn btn-success   w-md-auto'
                          >
                            {formik.isSubmitting ? (
                              <>
                                <div className='spinner-border spinner-border-sm me-2'></div>
                                {t('Processing')}
                              </>
                            ) : (
                              <>
                                <i className='bi bi-check2-circle me-2'></i>
                                {t('Close_Maintenance_Order')}
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </TabPane>
          </TabContent>

          <div className='d-flex justify-content-end flex-wrap mt-3'>
            <button type='button' className='btn btn-secondary w-100 w-md-auto' onClick={onClose}>
              {t('Close')}
            </button>
          </div>
        </CardBody>
      </Loading>

      {/* نموذج تأكيد أمر الصيانة */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className='fas fa-check-circle text-success me-2'></i>
            {t('ConfirmWorkOrder')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('AreYouSureConfirmWorkOrder')}</p>
          <Alert variant='info'>
            <i className='fas fa-info-circle me-2'></i>
            {t('WorkOrderConfirmationNote')}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowConfirmModal(false)}>
            {t('Cancel')}
          </Button>
          <Button variant='success' onClick={handleConfirmWorkOrder}>
            <i className='fas fa-check me-2'></i>
            {t('Confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );

  // r
};

// مكونات مساعدة
const TabContent = ({ children, activeTab }: any) => {
  return React.Children.map(children, child =>
    React.cloneElement(child, { isActive: child.props.tabId === activeTab })
  );
};

const TabPane = ({ tabId, isActive, children }: any) => {
  return isActive ? <div>{children}</div> : null;
};

export default ViewWorkOrder;
